/**
 * AuthContext — wraps Trimble Identity (TID) React SDK.
 *
 * When REACT_APP_TID_CLIENT_ID is not set (local dev with AUTH_BYPASS=true
 * on the backend), the provider operates in bypass mode: every user is treated
 * as an authenticated curator so the full UI is accessible without real tokens.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { TIDProvider, TIDClient, useAuth } from '@trimble-oss/trimble-id-react';
import { setTokenGetter, meApi } from '../services/api';

// ---------------------------------------------------------------------------
// Build a stable TIDClient singleton at module load time so it is never
// recreated on re-render (re-creation triggers the axios init path again).
// ---------------------------------------------------------------------------
function buildTIDClient(): TIDClient | null {
  const clientId = process.env.REACT_APP_TID_CLIENT_ID;
  if (!clientId) return null;

  const configurationEndpoint =
    process.env.REACT_APP_TID_CONFIGURATION_ENDPOINT ??
    'https://id.trimble.com/.well-known/openid-configuration';
  const redirectUrl =
    process.env.REACT_APP_TID_REDIRECT_URL ?? `${window.location.origin}/auth/callback`;
  const logoutRedirectUrl =
    process.env.REACT_APP_TID_LOGOUT_URL ?? `${window.location.origin}/logout-callback`;
  const scopeStr = process.env.REACT_APP_TID_SCOPES ?? 'openid profile email';
  const scopes = scopeStr.split(' ').filter(Boolean);

  const client = new TIDClient({
    config: { configurationEndpoint, clientId, redirectUrl, logoutRedirectUrl, scopes },
  });

  // The SDK's CacheManager uses InMemoryCache by default — tokens are lost on every page
  // refresh. Swap the cache storage for a localStorage-backed implementation so that the
  // SDK's own loadUserSession() / loadCacheSessionIntoSDK() can restore the session.
  // The cacheStorage field is private but there is no public API for persistent storage.
  const storageKey = `tid-session-${clientId}-${encodeURIComponent(scopes.join(' '))}`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (client as any).cacheManager.cacheStorage = {
    storeToken: async (token: unknown) => {
      try { localStorage.setItem(`${storageKey}-token`, JSON.stringify(token)); } catch { /* storage full / private mode */ }
    },
    getToken: async () => {
      try {
        const raw = localStorage.getItem(`${storageKey}-token`);
        return raw ? JSON.parse(raw) : undefined;
      } catch { return undefined; }
    },
    storeUser: async (user: unknown) => {
      try { localStorage.setItem(`${storageKey}-user`, JSON.stringify(user)); } catch { /* ignore */ }
    },
    getUser: async () => {
      try {
        const raw = localStorage.getItem(`${storageKey}-user`);
        return raw ? JSON.parse(raw) : undefined;
      } catch { return undefined; }
    },
    clear: async () => {
      try {
        localStorage.removeItem(`${storageKey}-token`);
        localStorage.removeItem(`${storageKey}-user`);
      } catch { /* ignore */ }
    },
  };

  return client;
}

const _tidClient = buildTIDClient();

type CypressWindow = Window & {
  Cypress?: {
    env: (key: string) => unknown;
  };
};

export interface LocalDemoProfile {
  name: string;
  token: string;
  label: string;
}

const LOCAL_DEMO_PROFILE_KEY = 'dewey.localDemoProfile';

export function getLocalDemoProfiles(): LocalDemoProfile[] {
  if (process.env.REACT_APP_ENV !== 'development') return [];
  return (process.env.REACT_APP_LOCAL_DEMO_PROFILES ?? '')
    .split(';')
    .map((entry) => {
      const [nameAndToken, label = ''] = entry.split('|', 2);
      const [name, token] = (nameAndToken ?? '').split('=', 2);
      return {
        name: (name ?? '').trim(),
        token: (token ?? '').trim(),
        label: label.trim(),
      };
    })
    .filter((profile) => profile.name && profile.token);
}

export function getSelectedLocalDemoProfileName(): string {
  return localStorage.getItem(LOCAL_DEMO_PROFILE_KEY) ?? '';
}

export function setSelectedLocalDemoProfileName(name: string): void {
  localStorage.setItem(LOCAL_DEMO_PROFILE_KEY, name);
}

function readCypressEnv(key: string): string {
  const w = window as CypressWindow;
  if (!w.Cypress) return '';
  const val = w.Cypress.env(key);
  return typeof val === 'string' ? val : '';
}

function getE2ETokenFromCypress(): string {
  return readCypressEnv('E2E_BEARER_TOKEN').trim();
}

function getLocalDemoToken(): string {
  if (process.env.REACT_APP_ENV !== 'development') return '';
  const configuredToken = (process.env.REACT_APP_E2E_BEARER_TOKEN ?? '').trim();
  if (configuredToken) return configuredToken;
  const profiles = getLocalDemoProfiles();
  const selectedName = getSelectedLocalDemoProfileName() || profiles[0]?.name;
  return profiles.find((profile) => profile.name === selectedName)?.token ?? '';
}

// ---------------------------------------------------------------------------
// Well-known role values — mirrors backend auth/claims.go
// ---------------------------------------------------------------------------
export type UserRole = 'viewer' | 'admin' | 'proposer' | 'curator' | 'mcp-agent';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------
export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  /** ABAC permission set. Always contains "viewer" when authenticated. */
  permissions: string[];
  /** Returns true if the user holds the given permission. */
  hasPermission: (permission: string) => boolean;
  /** The subject identifier from the backend /me response. */
  sub: string | null;
  email: string | null;
  login: () => void;
  logout: () => void;
  /** Returns the current bearer token (empty string in bypass mode). */
  getToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: true,
  isLoading: false,
  permissions: [],
  hasPermission: () => false,
  sub: null,
  email: null,
  login: () => {},
  logout: () => {},
  getToken: async () => '',
});

// ---------------------------------------------------------------------------
// Bypass mode (dev) — no real TID, mirrors the backend bypass permission set.
// ---------------------------------------------------------------------------
const BypassAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    setTokenGetter(async () => '');
  }, []);

  const allPermissions = ['viewer', 'proposer', 'curator', 'mcp-agent'];
  const value: AuthContextValue = {
    isAuthenticated: true,
    isLoading: false,
    permissions: allPermissions,
    hasPermission: (permission) => allPermissions.includes(permission),
    sub: 'dev-bypass',
    email: 'dev@bypass',
    login: () => {},
    logout: () => {},
    getToken: async () => '',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ---------------------------------------------------------------------------
// Cypress E2E mode — local token from Cypress.env, no interactive TID required.
// Permissions come from the backend so durable role changes are visible.
// ---------------------------------------------------------------------------
const E2EAuthProvider: React.FC<{ children: React.ReactNode; token: string }> = ({ children, token }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [subject, setSubject] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    setTokenGetter(async () => token);
    meApi
      .getMe()
      .then((me) => {
        setPermissions(me.permissions);
        setSubject(me.sub);
        setEmail(me.email);
      })
      .catch(() => {
        setPermissions(['viewer']);
        setSubject('e2e-local');
        setEmail('e2e-local@localhost');
      });
  }, [token]);

  const value: AuthContextValue = {
    isAuthenticated: true,
    isLoading: permissions.length === 0,
    permissions,
    hasPermission: (permission) => permissions.includes(permission),
    sub: subject,
    email,
    login: () => {},
    logout: () => {},
    getToken: async () => token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ---------------------------------------------------------------------------
// Inner consumer of the TID useAuth hook — bridges to our AuthContext
// ---------------------------------------------------------------------------
const TIDAuthBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user, getTokens, loginWithRedirect, logout } =
    useAuth();

  // Backend-authoritative permissions, populated via GET /api/v1/me
  const [backendPermissions, setBackendPermissions] = useState<string[]>([]);
  const [backendSub, setBackendSub] = useState<string | null>(null);
  const [, setCapsLoading] = useState(false);

  const getToken = async (): Promise<string> => {
    if (!isAuthenticated) return '';
    try {
      const tokens = await getTokens();
      return tokens.id_token || tokens.access_token;
    } catch {
      return '';
    }
  };

  // Register token getter synchronously during render so it is always up-to-date
  // before any child component's useEffect fires. A useEffect here would be too late:
  // child effects execute before parent effects in React's commit phase, meaning the
  // very first render where isAuthenticated=true would trigger data loads before the
  // getter had been updated with the fresh closure.
  setTokenGetter(getToken);

  // After authentication, fetch identity from the backend (authoritative permissions)
  useEffect(() => {
    if (!isAuthenticated) {
      setBackendPermissions([]);
      setBackendSub(null);
      return;
    }
    setCapsLoading(true);
    meApi
      .getMe()
      .then(me => {
        setBackendPermissions(me.permissions);
        setBackendSub(me.sub);
      })
      .catch(err => {
        console.warn('[AuthContext] /me fetch failed, permissions will be empty', err);
        setBackendPermissions([]);
      })
      .finally(() => setCapsLoading(false));
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasPermission = (permission: string) => backendPermissions.includes(permission);

  const value: AuthContextValue = {
    isAuthenticated: isAuthenticated ?? false,
    isLoading: isLoading ?? false,
    permissions: backendPermissions,
    hasPermission,
    sub: backendSub,
    email: (user as { email?: string } | null | undefined)?.email ?? null,
    login: () => loginWithRedirect?.(),
    logout: () => logout?.(),
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ---------------------------------------------------------------------------
// Main provider — decides bypass vs real TID
// ---------------------------------------------------------------------------
interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const e2eToken = getE2ETokenFromCypress() || getLocalDemoToken();
  if (e2eToken) {
    return <E2EAuthProvider token={e2eToken}>{children}</E2EAuthProvider>;
  }

  // No TID client configured → bypass mode
  if (!_tidClient) {
    return <BypassAuthProvider>{children}</BypassAuthProvider>;
  }

  // Clean up the OAuth callback URL without triggering a full page reload.
  // TIDProvider has already exchanged the code and stored tokens before calling
  // this; using history.replaceState keeps the page mounted so the auth state
  // (isAuthenticated=true) is immediately visible in TIDAuthBridge.
  const handleRedirect = () => {
    window.history.replaceState({}, document.title, '/');
  };

  return (
    <TIDProvider
      tidClient={_tidClient}
      onRedirectCallback={handleRedirect}
      checkRedirectUrlMatch={true}
    >
      <TIDAuthBridge>{children}</TIDAuthBridge>
    </TIDProvider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export const useAuthContext = (): AuthContextValue => useContext(AuthContext);
